import { computeReinforcements, territoryCount, vocationOf } from '../selectors';
import type { PlayerId, TurnState } from '../types';
import { bossPhase } from './boss';
import { drawTerritoryCard } from './cards';
import type { Ctx } from './context';
import { serverSave } from './missions';
import { expireRoundStart, expireTurnEnd, expireTurnStart } from './effects';

export function freshTurn(): TurnState {
  return { conquered: 0, lost: 0, powerUsed: false, movedIn: {}, attackedFrom: {}, bountyClaimed: false, harmonyGained: 0 };
}

function firstAlive(ctx: Ctx): PlayerId {
  return ctx.s.players.find((p) => p.alive)!.id;
}

/** Começa uma rodada: fase de boss e depois o turno do primeiro jogador vivo. */
export function startRound(ctx: Ctx): void {
  const { s } = ctx;
  s.round++;
  ctx.events.push({ type: 'roundStart', round: s.round });
  expireRoundStart(ctx, s.round);
  if (s.info.ruleset.bounty) markBounty(ctx);
  s.current = firstAlive(ctx);
  if (s.info.ruleset.addons.bosses) {
    s.phase = 'boss';
    bossPhase(ctx);
    if (s.pending) return; // o mestre dos bosses escolhe a região
  }
  startTurn(ctx, s.current);
}

/** Cabeça a prêmio: o jogador vivo com mais territórios, sem empate. */
function markBounty(ctx: Ctx): void {
  const { s } = ctx;
  const counts = s.players.filter((p) => p.alive).map((p) => ({ id: p.id, n: territoryCount(s, p.id) }));
  counts.sort((a, b) => b.n - a.n);
  const top = counts[0] && (!counts[1] || counts[0].n > counts[1].n) ? counts[0].id : null;
  s.bounty = top;
  ctx.events.push({ type: 'bounty', player: top });
}

export function startTurn(ctx: Ctx, p: PlayerId): void {
  const { s } = ctx;
  s.current = p;
  s.phase = 'reinforce';
  expireTurnStart(ctx, p);
  s.turn = freshTurn();
  s.defenseUsed = [];
  // A caveira dura até o próximo turno de quem a ganhou.
  if (s.skulls.includes(p)) s.skulls = s.skulls.filter((x) => x !== p);
  s.reinforcements = computeReinforcements(s, p);
  ctx.events.push({ type: 'turnStart', player: p, round: s.round, reinforcements: s.reinforcements });
}

/** Saída da fase de remanejar. A cura do druida pode pedir uma escolha antes. */
export function endTurn(ctx: Ctx): void {
  const { s } = ctx;
  const p = s.current;
  if (vocationOf(s, p) === 'heal' && s.turn.lost > 0) {
    s.pending = { kind: 'heal', player: p };
    return;
  }
  finishTurn(ctx);
}

export function finishTurn(ctx: Ctx): void {
  const { s } = ctx;
  const p = s.current;
  if (s.turn.conquered > 0) drawTerritoryCard(ctx, p);
  expireTurnEnd(ctx, p);
  s.attacked[p] = [];
  const n = s.players.length;
  for (let i = 1; i <= n; i++) {
    const idx = (p + i) % n;
    if (!s.players[idx].alive) continue;
    if (idx <= p) {
      // virou a rodada
      if (s.round >= s.info.ruleset.serverSaveRound) {
        serverSave(ctx);
        return;
      }
      startRound(ctx);
      return;
    }
    startTurn(ctx, idx);
    return;
  }
}
